import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Example Redux Slice
 *
 * This is a template showing how to create a Redux slice with:
 * - State interface
 * - Initial state
 * - Reducers (sync actions)
 * - Async thunks
 * - Selectors
 *
 * Copy this file and modify it for your own slices.
 * Delete this file when you don't need the example anymore.
 */

// Define the state interface
interface ExampleState {
  items: string[];
  loading: boolean;
  error: string | null;
}

// Define the initial state
const initialState: ExampleState = {
  items: [],
  loading: false,
  error: null,
};

// Async thunk example
export const fetchItems = createAsyncThunk(
  "example/fetchItems",
  async (_, { rejectWithValue }) => {
    try {
      // Replace with your actual API call
      const response = await fetch("/api/items");
      if (!response.ok) {
        throw new Error("Failed to fetch items");
      }
      const data = await response.json();
      return data as string[];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// Create the slice
const exampleSlice = createSlice({
  name: "example",
  initialState,
  reducers: {
    // Sync action: Add an item
    addItem: (state, action: PayloadAction<string>) => {
      state.items.push(action.payload);
    },

    // Sync action: Remove an item
    removeItem: (state, action: PayloadAction<number>) => {
      state.items.splice(action.payload, 1);
    },

    // Sync action: Clear all items
    clearItems: (state) => {
      state.items = [];
    },

    // Sync action: Reset state
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchItems pending
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Handle fetchItems fulfilled
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      // Handle fetchItems rejected
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { addItem, removeItem, clearItems, resetState } =
  exampleSlice.actions;

// Export reducer
export default exampleSlice.reducer;

// Selectors
export const selectItems = (state: { example: ExampleState }) =>
  state.example.items;
export const selectLoading = (state: { example: ExampleState }) =>
  state.example.loading;
export const selectError = (state: { example: ExampleState }) =>
  state.example.error;
